import AsyncStorage from '@react-native-async-storage/async-storage';
import { Picker } from '@react-native-picker/picker';
import axios from 'axios';
import { Formik } from 'formik';
import { globalStyles } from '../../../styles/globalStyles';
import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import {
  ActivityIndicator,
  Dimensions,
  KeyboardAvoidingView,
  TouchableWithoutFeedback,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  Image,
  LogBox,
  Pressable,
  Modal,
} from 'react-native';
import { BASE_URL } from '../../../components/BaseUrl';
import AppBtn from '../../../shared/appBtn';
import AnimatedArrow from '../../../shared/AnimatedArrow';
import WavyTopBar from '../../../shared/WavyTopBar';
import ImageGallery from '../../../shared/ImageGallery';
import LeftBar from '../../../shared/LeftBar';
import LeftBarItem from '../../../shared/LeftBarItem';
import DateTimePickerModal from 'react-native-modal-datetime-picker';
import moment from 'moment';
import ErrorText from '../../../shared/errorText';
import DropDownFormik from '../../../shared/DropDownFormik';
import { DatePickerModal } from 'react-native-paper-dates';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import * as yup from 'yup';
import { ThemeOne } from '../../../utils/constants/Colors';
import CustomDropdown from '../../../shared/CustomDropdown';
import LeftBarItemAdmin from '../../../shared/LeftBarItemAdmin';
import CustomDropdownThana from '../../../shared/CustomDropDownThana';

// ─── Constants ────────────────────────────────────────────────────────────────

const Colors2 = ThemeOne;
const { width: windowWidth, height: windowHeight } = Dimensions.get('window');
const IS_WEB = Platform.OS === 'web';
const IS_WEB_WIDE = Platform.OS === 'web' && windowWidth >= 600;
const IS_DESKTOP_FORM = Platform.OS === 'web' && windowWidth >= 1024;
const IS_TABLET_FORM = Platform.OS === 'web' && windowWidth >= 600 && windowWidth < 1024;

const COURT_TYPE_DISTRICT = [
  { id: '1', name: 'জি আর শাখার আদালত', officeOriginIds: [], type: '0' },
  {
    id: '3',
    name: 'চীফ জুডিসিয়াল ম্যাজিস্ট্রেট আদালত',
    officeOriginIds: [14, 15, 16, 17, 84, 85, 91, 95],
    type: '1',
  },
  {
    id: '2',
    name: 'জেলা জজ আদালত',
    officeOriginIds: [4, 5, 6, 7, 8, 18, 19, 20, 21, 74, 75, 76, 83, 94, 103, 105],
    type: '1',
  },

  {
    id: '4',
    name: 'ট্রাইবুনালসমূহ',
    officeOriginIds: [89, 9, 72, 73, 77, 79, 80, 81, 82, 86, 87, 96, 102, 104, 110, 111, 112],
    type: '1',
  },
];

const COURT_TYPE_METRO = [
  { id: '1', name: 'জি আর শাখার আদালত', officeOriginIds: [], type: '0' },
  {
    id: '6',
    name: 'চীফ মেট্রোপলিটন ম্যাজিস্ট্রেট আদালত',
    officeOriginIds: [13, 22, 23, 24, 78, 92, 100],
    type: '2',
  },
  { id: '5', name: 'মহানগর দায়রা জজ আদালত', officeOriginIds: [10, 11, 12, 88], type: '2' },

  { id: '7', name: 'বিশেষ ট্রাইব্যুনাল', officeOriginIds: [109], type: '2' },
];

const METRO_DISTRICT_IDS = [65, 66, 67, 68, 69, 70];

const BANGLA_DIGITS = {
  0: '০', 1: '১', 2: '২', 3: '৩', 4: '৪',
  5: '৫', 6: '৬', 7: '৭', 8: '৮', 9: '৯',
};

const PAST_DATE = new Date(new Date().setDate(new Date().getDate() - 500000));
const FUTURE_DATE = new Date(new Date().setDate(new Date().getDate() + 500000));

// ─── Validation Schema ────────────────────────────────────────────────────────

const searchSchema = yup.object({
  divisionName: yup.string().required('Division is required'),
  districtName: yup.string().required('District is required'),
  lowerCourtName: yup
    .string()
    .nullable()
    .test('not-empty', 'Lower Court is required', val => val != null && val !== '')
    .required('Lower Court is required'),
  courtTypeName: yup.string().required("Court is required")
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const engToBdNum = str => {
  let result = String(str);
  for (const digit in BANGLA_DIGITS) {
    result = result.replace(new RegExp(digit, 'g'), BANGLA_DIGITS[digit]);
  }
  return result;
};

const replaceTextDate = str => {
  const yearMap = {
    '2023': '২০২৩', '2024': '২০২৪', '2025': '২০২৫', '2026': '২০২৬',
    '2027': '২০২৭', '2028': '২০২৮', '2029': '২০২৯', '2030': '২০৩০',
  };
  let result = String(str);
  for (const [en, bn] of Object.entries(yearMap)) {
    result = result.replace(en, bn);
  }
  return engToBdNum(result);
};

const replaceTextDay = str => {
  const dayMap = {
    Saturday: 'শনিবার', Sunday: 'রবিবার', Monday: 'সোমবার',
    Tuesday: 'মঙ্গলবার', Wednesday: 'বুধবার', Thursday: 'বৃহস্পতিবার',
    Friday: 'শুক্রবার',
  };
  let result = String(str);
  for (const [en, bn] of Object.entries(dayMap)) {
    result = result.replace(en, bn);
  }
  return result;
};

const isPlaceholder = value => value == null;

const buildFormBody = obj =>
  Object.entries(obj)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

// ─── API ──────────────────────────────────────────────────────────────────────

const fetchDivisions = async () => {
  const { data } = await axios.get(`${BASE_URL}/GetDivission`);
  return Array.isArray(data) ? data : [];
};

const fetchDistricts = async divisionId => {
  const { data } = await axios.post(
    `${BASE_URL}/DivisionToDistrict`,
    buildFormBody({ division_id: divisionId }),
  );
  return Array.isArray(data) ? data : [];
};

const fetchAllLowerCourts = async (districtId, divisionId) => {
  const { data } = await axios.post(
    `${BASE_URL}/DivisionAndDistrictToCourtName`,
    buildFormBody({ geo_district_id: districtId, geo_division_id: divisionId }),
  );
  return Array.isArray(data) ? data : [];
};

const fetchGrBranches = async (districtId, divisionId) => {
  const { data } = await axios.get(`${BASE_URL}/GetGrbranchByDivisionAndDistrict`, {
    params: { geo_district_id: districtId, geo_division_id: divisionId, status: 1 },
  });
  return Array.isArray(data) ? data : [];
};

const fetchPoliceStations = async (districtId, divisionId, grBranchId) => {
  if (!grBranchId) return [];
  const { data } = await axios.get(`${BASE_URL}/GetAllPoliceStation`, {
    params: {
      geo_district_id: districtId,
      geo_division_id: divisionId,
      gr_branch_id: grBranchId,
      status: 1,
    },
  });
  return Array.isArray(data) ? data : [];
};

const fetchCourtImages = async (lowerCourtId, causeDate) => {
  if (!lowerCourtId) return [];
  const { data, status } = await axios.get(`${BASE_URL}/GetCourtImages`, {
    params: { lower_court_id: lowerCourtId, status: 1, cause_date: causeDate },
  });
  if (status !== 200) throw new Error('Failed to fetch court images');
  return Array.isArray(data) ? data : [];
};

const fetchThanaImages = async (policeStationId, causeDate) => {
  if (!policeStationId) return [];
  const { data, status } = await axios.get(`${BASE_URL}/GetThanaImages`, {
    params: { diary_police_station_id: policeStationId, status: 1, cause_date: causeDate },
  });
  if (status !== 200) throw new Error('Failed to fetch thana images');
  return Array.isArray(data) ? data : [];
};

const normalizePoliceStations = stations =>
  (Array.isArray(stations) ? stations : [])
    .map((station, index) => ({
      ...station,
      value: String(
        station?.value ??
        station?.id ??
        station?.police_station_id ??
        station?.offline_police_station_id ??
        station?.thana_id ??
        index
      ),
      label:
        station?.label ??
        station?.name_bng ??
        station?.police_station_name_bng ??
        station?.police_station_name ??
        station?.station_name_bng ??
        station?.station_name ??
        station?.thana_name_bng ??
        station?.thana_name ??
        station?.name ??
        '',
    }))
    .filter(station => station.label);

const getUserIdFromStorage = async () => {
  try {
    const json = await AsyncStorage.getItem('user_id');
    return json !== null ? JSON.parse(json) : null;
  } catch {
    return null;
  }
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function DailyCaseListScreenGROffline() {
  useNavigation(); // keep navigation context alive if needed by children

  // ── Refs ─────────────────────────────────────────────────────────────────────
  const divisionIdRef = useRef(null);
  const districtIdRef = useRef(null);
  const allLowerCourtsRef = useRef([]);
  const isGrBranchRef = useRef(false);
  const route = useRoute();
  // ── State ────────────────────────────────────────────────────────────────────
  const [divisionId, setDivisionId] = useState(null);
  const [districtId, setDistrictId] = useState(null);
  const [courtTypeId, setCourtTypeId] = useState(null);
  const [selectedCourtId, setSelectedCourtId] = useState(null);
  const [policeStationId, setPoliceStationId] = useState(null);
  const [lowerCourtOpen, setLowerCourtOpen] = useState(false);

  const [divisions, setDivisions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [lowerCourts, setLowerCourts] = useState([]);
  const [courtTypes, setCourtTypes] = useState([]);
  const [policeStations, setPoliceStations] = useState([]);

  const [districtName, setDistrictName] = useState('');
  const [courtName, setCourtName] = useState('');
  const [courtOfficeName, setCourtOfficeName] = useState('');

  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [webDate, setWebDate] = useState(new Date());
  const [webDatePickerVisible, setWebDatePickerVisible] = useState(false);

  const [imageList, setImageList] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLowerCourtDropdownOpen, setIsLowerCourtDropdownOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const responsiveFieldWidth = IS_DESKTOP_FORM ? 200 : '100%';
  // ── Derived values ───────────────────────────────────────────────────────────
  const activeDate = IS_WEB ? webDate : selectedDate;
  const formattedCauseDate = activeDate
    ? moment(activeDate).format('YYYY-MM-DD')
    : moment().format('YYYY-MM-DD');
  const displayDate = activeDate
    ? engToBdNum(moment(activeDate).format('DD-MM-YYYY'))
    : engToBdNum(moment().format('DD-MM-YYYY'));
  const displayDay = activeDate
    ? replaceTextDay(moment(activeDate).format('dddd'))
    : '';

  // Prepend placeholder item - using empty string instead of null
  const courtsWithPlaceholder = useMemo(
    () => [
      { label: 'আদালত নির্বাচন করুন', value: '', labelStyle: { color: 'red' } },
      ...(lowerCourts || []).map(court => ({
        ...court,
        value: court.value?.toString() || court.id?.toString(), // Ensure value is string
        label: court.label || court.office_name_bng || court.name_bng
      })),
    ],
    [lowerCourts],
  );

  // ── Lifecycle ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (Platform.OS === 'web') {
      window.history.pushState(null, null, document.URL);
    }
    LogBox.ignoreLogs([
      'VirtualizedLists should never be nested inside plain ScrollViews',
    ]);
    getUserIdFromStorage().catch(console.error);
    fetchDivisions().then(setDivisions).catch(console.error);
  }, []);

  useFocusEffect(
    useCallback(() => {
      setIsLoading(false);
      setHasSearched(false);
      setImageList([]);
    }, []),
  );

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleDivisionChange = useCallback((itemValue, formikHandleChange) => {
    divisionIdRef.current = itemValue;
    setDivisionId(itemValue);
    formikHandleChange(itemValue);

    setDistricts([]);
    setDistrictId(null);
    districtIdRef.current = null;
    setCourtTypes([]);
    setCourtTypeId(null);
    setLowerCourts([]);
    setSelectedCourtId(null);
    setPoliceStations([]);
    setPoliceStationId(null);
    allLowerCourtsRef.current = [];

    fetchDistricts(itemValue).then(setDistricts).catch(console.error);
  }, []);

  // In handleDistrictChange, remove the fetchAllLowerCourts call since it's no longer needed
  const handleDistrictChange = useCallback(
    (itemValue, itemIndex, formikHandleChange) => {
      districtIdRef.current = itemValue;
      setDistrictId(itemValue);
      formikHandleChange(itemValue);
      setDistrictName(districts[itemIndex - 1]?.district_name_bng ?? '');

      setCourtTypes([]);
      setCourtTypeId(0);
      setLowerCourts([]);
      setSelectedCourtId(null);
      setPoliceStations([]);
      setPoliceStationId(null);
      allLowerCourtsRef.current = [];

      // Remove this call since we'll fetch courts when court type is selected
      // fetchAllLowerCourts(itemValue, divisionIdRef.current)

      // Just set court types based on district
      setCourtTypes(
        METRO_DISTRICT_IDS.includes(Number(itemValue))
          ? COURT_TYPE_METRO
          : COURT_TYPE_DISTRICT,
      );
    },
    [districts],
  );

  // Add this helper function near your other API functions
  const fetchOfflineLowerCourts = async (divisionId, districtId, courtTypeId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/GetOfflineLowerCourtByDivisionAndDistrict`,
        {
          params: {
            geo_division_id: divisionId,
            geo_district_id: districtId,
            building_name_id: courtTypeId,
            status: 1,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching offline lower courts:', error);
      return [];
    }
  };

  // Then update handleCourtTypeChange to use this helper
  const handleCourtTypeChange = useCallback(async itemValue => {
  
    setLowerCourts([]);
    setSelectedCourtId(null);
    setPoliceStations([]);
    setPoliceStationId(null);
    setCourtName('');
    setCourtOfficeName('');

    const isGr = Number(itemValue) === 1;
    isGrBranchRef.current = isGr;

    if (isGr) {
      try {
        const branches = await fetchGrBranches(
          districtIdRef.current,
          divisionIdRef.current,
        );
        setLowerCourts(
          branches.map(b => ({ ...b, value: String(b.id), label: b.name_bng })),
        );
      } catch (err) {
        console.error('handleCourtTypeChange GR Error:', err);
      }
      return;
    }

    // For non-GR courts, use the new API
    const courts = await fetchOfflineLowerCourts(
      divisionIdRef.current,
      districtIdRef.current,
      itemValue
    );

    if (Array.isArray(courts) && courts.length > 0) {
      setLowerCourts(
        courts.map(court => ({
          ...court,
          value: String(court.id || court.lower_court_id),
          label: court.office_name_bng || court.name_bng
        })),
      );
    } else {
      setLowerCourts([]);
    }
  }, []);

  // Add these helper functions near your other API functions
  const fetchGRPoliceStations = async (districtId, divisionId, grBranchId) => {
    if (!grBranchId) return [];
    const { data } = await axios.get(`${BASE_URL}/GetAllPoliceStation`, {
      params: {
        geo_district_id: districtId,
        geo_division_id: divisionId,
        gr_branch_id: grBranchId,
        status: 1,
      },
    });
    return Array.isArray(data) ? data : [];
  };

  const fetchOfflinePoliceStations = async (lowerCourtId) => {
    if (!lowerCourtId) return [];
    try {
      const response = await axios.get(
        `${BASE_URL}/GetOfflinePoliceByStationLowerCourt`,
        {
          params: {
            offline_lower_courts_id: lowerCourtId,
            status: 1,
          },
        }
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching offline police stations:', error);
      return [];
    }
  };

  // Then simplify handleLowerCourtChange
  const handleLowerCourtChange = useCallback(
    async (value, formikSetFieldValue) => {
      // Handle empty string or null
      if (value == null || value === '') {
        setSelectedCourtId(null);
        setCourtName('');
        setPoliceStations([]);
        setPoliceStationId(null);
        setCourtOfficeName('');
        formikSetFieldValue('lowerCourtName', '');
        formikSetFieldValue('policeStationName', '');
        return;
      }

      // Convert to string for consistency
      const stringValue = String(value);
      setSelectedCourtId(stringValue);
      formikSetFieldValue('lowerCourtName', stringValue);
      const found = lowerCourts.find(c =>
        String(c.value) === stringValue || String(c.id) === stringValue
      );
      setCourtName(found?.office_name_bng ?? found?.name_bng ?? found?.label ?? '');
      setCourtOfficeName('');

      try {
        let stations = [];

        if (isGrBranchRef.current) {
          // GR branch: use existing fetchPoliceStations
          stations = await fetchGRPoliceStations(
            districtIdRef.current,
            divisionIdRef.current,
            value,
          );
        } else {
          // Non-GR courts: use new API
          stations = await fetchOfflinePoliceStations(stringValue);
        }

        setPoliceStations(normalizePoliceStations(stations));

        // Reset police station selection when lower court changes
        setPoliceStationId(null);
        formikSetFieldValue('policeStationName', '');

      } catch (err) {
        console.error('handleLowerCourtChange Error:', err);
        setPoliceStations([]);
        setPoliceStationId(null);
        formikSetFieldValue('policeStationName', '');
      }
    },
    [lowerCourts],
  );

  const handlePoliceStationChange = useCallback(
    (itemValue, formikHandleChange) => {
      setPoliceStationId(itemValue);
      formikHandleChange(itemValue);
      const found = policeStations.find(
        s => String(s.value) === String(itemValue) || String(s.id) === String(itemValue),
      );
      setCourtOfficeName(found?.name_bng ?? found?.name ?? found?.label ?? '');
    },
    [policeStations],
  );


  const handlePoliceStationSelect = (item) => {
    setPoliceStationId(item.value);
    setPoliceStationName(item.label);   // নাম সেট করুন
  };
  const handleSearch = useCallback(
    async values => {
      // Check for empty string
      if (!values.lowerCourtName || values.lowerCourtName === '') {
        alert('আদালত নির্বাচন করুন');
        return;
      }

      setIsLoading(true);
      setHasSearched(false);
      setImageList([]);

      try {
        let images = [];
        if (isGrBranchRef.current) {
          if (!policeStationId) {
            alert('থানা/অঞ্চল নির্বাচন করুন');
            setIsLoading(false);
            return;
          }
          images = await fetchThanaImages(policeStationId, formattedCauseDate);
        } else {
          images = await fetchCourtImages(values.lowerCourtName, formattedCauseDate);
        }
        setImageList(images);
      } catch (err) {
        console.error('handleSearch Error:', err);
        setImageList([]);
      } finally {
        setIsLoading(false);
        setHasSearched(true);
      }
    },
    [policeStationId, formattedCauseDate],
  );

  const handleImagePress = useCallback(image => {
    setSelectedImage(image);
    setImageModalVisible(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setImageModalVisible(false);
  }, []);

  const closeDropdown = useCallback(() => setLowerCourtOpen(false), []);

  const showDatePicker = useCallback(() => setDatePickerVisible(true), []);
  const hideDatePicker = useCallback(() => setDatePickerVisible(false), []);

  const handleMobileDateConfirm = useCallback(
    val => {
      setSelectedDate(val);
      hideDatePicker();
    },
    [hideDatePicker],
  );

  const handleWebDateDismiss = useCallback(() => setWebDatePickerVisible(false), []);
  const handleWebDateConfirm = useCallback(({ date }) => {
    setWebDatePickerVisible(false);
    if (date) setWebDate(date);
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <View
      style={IS_WEB_WIDE ? globalStyles.mainContainer : globalStyles.mainContainerMobile}
    >
      <LeftBar>
        <LeftBarItem
          selectcolor="true"
          name="দৈনিক কার্যতালিকা "
          stack="দৈনিক কার্যতালিকা"
          screen=""
        />

        <LeftBarItemAdmin
          activeScreen={route.name}
          name="অফলাইন কার্যতালিকা ✔️"
          stack="দৈনিক কার্যতালিকা"
          screen="DailyCaseListScreen" />

        <LeftBarItemAdmin
          activeScreen={route.name}
          name="অনলাইন কার্যতালিকা"
          stack="দৈনিক কার্যতালিকা"
          screen="OnlineCaseListScreen"
        />
      </LeftBar>

      <View
        style={IS_WEB_WIDE ? globalStyles.bodyContainer : globalStyles.bodyContainerMobile}
      >
        <TouchableWithoutFeedback onPress={closeDropdown}>
          <View style={{ backgroundColor: '#fff' }}>
            <View style={IS_WEB_WIDE ? styles.headerFixed : null}>
              <KeyboardAvoidingView
                style={styles.searchContainer}
                behavior="padding"
                enabled
              >
                <WavyTopBar customStyles={[styles.backgroundImage, { zIndex: -1 }]} />

                <Formik
                  enableReinitialize
                  initialValues={{
                    searchDate: '',
                    divisionName: '',
                    districtName: '',
                    lowerCourtName: '',
                    courtTypeName: '',
                    policeStationName: '',
                  }}
                  validationSchema={searchSchema}
                  onSubmit={handleSearch}
                >
                  {({ errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, values }) => (
                    <>
                      <View style={[styles.formGroup, isLowerCourtDropdownOpen && styles.dropdownOpenGroup]}>
                        <View style={[styles.pickerWrapper, isLowerCourtDropdownOpen && styles.dropdownOpenWrapper]}>
                          <Picker
                            selectedValue={divisionId ?? 0}
                            style={isPlaceholder(divisionId) ? styles.pickerPlaceholder : styles.picker}
                            onValueChange={(val, idx) =>
                              handleDivisionChange(val, handleChange('divisionName'))
                            }
                            mode="dropdown"
                          >
                            <Picker.Item label="বিভাগ" value="0" color="red" />
                            {divisions.map((item, idx) => (
                              <Picker.Item
                                key={idx}
                                color="#333"
                                label={item.division_name_bng.toString()}
                                value={item.geo_division_id.toString()}
                              />
                            ))}
                          </Picker>
                        </View>
                        <View style={styles.errorWrapper}>

                          {touched.divisionName && errors.divisionName && (
                            <ErrorText >{errors.divisionName}</ErrorText>
                          )}
                        </View>
                      </View>

                      <View style={styles.formGroup}>
                        <View style={styles.pickerWrapper}>
                          <Picker
                            selectedValue={districtId ?? 0}
                            style={isPlaceholder(districtId) ? styles.pickerPlaceholder : styles.picker}
                            onValueChange={(val, idx) =>
                              handleDistrictChange(val, idx, handleChange('districtName'))
                            }
                            mode="dropdown"
                          >
                            <Picker.Item label="জেলা" value="0" color="red" />
                            {districts.map((item, idx) => (
                              <Picker.Item
                                key={idx}
                                color="#333"
                                label={item.district_name_bng.toString()}
                                value={item.geo_district_id.toString()}
                              />
                            ))}
                          </Picker>
                        </View>
                        <View style={styles.errorWrapper}>
                          {touched.districtName && errors.districtName && (
                            <ErrorText>{errors.districtName}</ErrorText>
                          )}
                        </View>

                      </View>

                      <View style={styles.formGroup}>
                        <View style={styles.pickerWrapper}>
                          <Picker
                            selectedValue={courtTypeId}
                            style={[
                              isPlaceholder(courtTypeId)
                                ? styles.pickerPlaceholder
                                : styles.picker,
                              IS_DESKTOP_FORM ? { width: 150 } : null,
                            ]}
                            dropdownIconColor="#1e3a8a"
                            onValueChange={val => {
                              setCourtTypeId(val)
                              handleCourtTypeChange(val);
                              setFieldValue('lowerCourtName', '');
                              setSelectedCourtId(null);
                              handleChange('courtTypeName')(val);
                              
                            }}
                            mode="dropdown"
                          >
                            <Picker.Item label="আদালতের ধরন" value="0" color="red" />
                            {courtTypes.map((item, idx) => (
                              <Picker.Item
                                key={idx}
                                label={`${item.name}`}
                                value={item.id.toString()}
                                color="#333"
                              />
                            ))}
                          </Picker>
                        </View>
                      </View>

                      <View style={styles.formGroup}>
                        <View style={styles.pickerWrapper}>
                          <View
                            style={{
                              flexDirection: 'row',
                              height: windowWidth >= 600 ? 27 : 40,
                              zIndex: 100,
                              width: '100%',
                            }}
                          >
                            <CustomDropdown
                              data={courtsWithPlaceholder}
                              selectedValue={selectedCourtId || ''}
                              onSelect={(value) => {
                                const actualValue = value === '' ? null : value;
                                handleLowerCourtChange(actualValue, setFieldValue);
                              }}
                              placeholder="আদালত নির্বাচন করুন"
                              placeholderTextColor="#989898"
                              selectedTextColor="#333333"
                           
                              dropdownButtonStyle={{
                                width: responsiveFieldWidth + Number(50),
                                height: windowWidth >= 600 ? 27 : 40,
                                minHeight: windowWidth >= 600 ? 27 : 40,
                                zIndex: 100,
                              }}
                              selectOptionsWidth={350}
                              dropdownListStyle={{}}
                              itemStyle={{}}
                              itemTextStyle={{}}
                              searchPlaceholder="আদালত সার্চ করুন..."
                              showSearch={null}
                              maxHeight={390}
                              zIndex={9999999}
                              onOpenChange={setIsLowerCourtDropdownOpen} // Pass the setter
                            />
                          </View>
                        </View>
                        {!isLowerCourtDropdownOpen && (
                          <View style={styles.errorWrapper}>
                            {touched.lowerCourtName && errors.lowerCourtName && (
                              <ErrorText>{errors.lowerCourtName}</ErrorText>
                            )}
                          </View>
                        )}
                      </View>

                      {
                        Number(courtTypeId) == 1 && (
                          <View style={[styles.formGroup, openDropdown === 'policeStation' && styles.dropdownOpenGroup]}>
                            <View style={[styles.pickerWrapper, openDropdown === 'policeStation' && styles.dropdownOpenWrapper, { borderWidth: 0, border: "none" }]}>
                              <View
                                style={{
                                  flexDirection: 'row',
                                  height: windowWidth >= 600 ? 27 : 40,
                                  zIndex: 100,
                                  width: '100%',
                                  marginLeft: IS_WEB_WIDE? -0 : -10
                                }}
                              >
                                <CustomDropdownThana
                                  data={policeStations}
                                  selectedValue={policeStationId}
                                  onValueChange={(val) =>
                                    handlePoliceStationChange(val, handleChange('policeStationName'))
                                  }
                                  placeholderTextColor={'#989898'}
                                  containerStyle={{ width: responsiveFieldWidth - Number(50) }}
                                  dropdownButtonStyle={{
                                    width: responsiveFieldWidth - Number(50),
                                    height: windowWidth >= 600 ? 27 : 40,
                                  }}
                                  placeholder="থানা/এখতিয়ার নির্বাচন করুন"
                                  showSearch={false}
                                  maxHeight={400}
                                  onOpenChange={(isOpen) => {
                                    if (isOpen) setOpenDropdown('policeStation');
                                    else if (openDropdown === 'policeStation') setOpenDropdown(null);
                                  }}
                                />

                              </View>


                            </View>
                          </View>
                        )
                      }



                      <View style={styles.formGroup}>
                        <View style={styles.dateFieldRow}>
                          {IS_WEB ? (
                            <TouchableOpacity
                              onPress={() => setWebDatePickerVisible(true)}
                              style={styles.dateInputWrapper}
                            >
                              <TextInput
                                style={styles.dateInput}
                                value={displayDate}
                                onChangeText={handleChange('searchDate')}
                                onBlur={handleBlur('searchDate')}
                                placeholder="DD-MM-YYYY"
                                underlineColorAndroid="transparent"
                                editable={false}
                                pointerEvents="none"
                              />
                              <Image
                                source={{
                                  uri: 'https://bdjudgecourt.com/assets/calendar.png',
                                }}
                                style={styles.calendarIcon}
                              />
                            </TouchableOpacity>
                          ) : (

                            <TouchableOpacity
                              onPress={showDatePicker}
                              style={styles.dateInputWrapper}
                            >
                              <TextInput
                                style={styles.dateInput}
                                value={displayDate}
                                onChangeText={handleChange('searchDate')}
                                onBlur={handleBlur('searchDate')}
                                placeholder="DD-MM-YYYY"
                                underlineColorAndroid="transparent"
                                onTouchStart={showDatePicker}
                              />
                              <Image
                                source={{
                                  uri: 'https://bdjudgecourt.com/assets/calendar.png',
                                }}
                                style={styles.calendarIcon}
                              />
                            </TouchableOpacity>

                          )}
                        </View>

                        {IS_WEB ? (
                          <View
                            style={{ justifyContent: 'center', flex: 1, alignItems: 'center' }}
                          >
                            <DatePickerModal
                              locale="en-GB"
                              mode="single"
                              visible={webDatePickerVisible}
                              onDismiss={handleWebDateDismiss}
                              date={webDate}
                              onConfirm={handleWebDateConfirm}
                              onChange={handleWebDateConfirm}
                              validRange={{
                                startDate: PAST_DATE,
                                disabledDates: [FUTURE_DATE],
                              }}
                              style={{ width: 300, height: 400 }}
                              presentationStyle="formSheet"
                            />
                          </View>
                        ) : (
                          <DateTimePickerModal
                            isVisible={datePickerVisible}
                            mode="date"
                            date={selectedDate ?? new Date()}
                            onConfirm={handleMobileDateConfirm}
                            onCancel={hideDatePicker}
                          />
                        )}
                      </View>

                      <View style={{ flexDirection: 'row', marginTop: 10 }}>
                        <View style={styles.searchButtonWrapper}>
                          <AppBtn
                            title="অনুসন্ধান"
                            onPress={handleSubmit}
                            width={IS_DESKTOP_FORM ? 80 : "100%"}
                            fontwidth="bold"
                            disabled={isLoading}
                          />
                        </View>
                      </View>
                    </>
                  )}
                </Formik>
              </KeyboardAvoidingView>

              {isLoading ? (
                <View style={{ height: 700, paddingVertical: 200 }}>
                  <ActivityIndicator size="large" />
                </View>
              ) : (
                <View style={styles.resultsWrapper}>
                  <View style={styles.tabWrapper}>
                    <Pressable
                      style={({ pressed }) => [
                        styles.tabButton,
                        styles.activeTabButton,
                        pressed && { opacity: 0.8 },
                      ]}
                      hitSlop={{ top: 10, bottom: 10, left: 5, right: 5 }}
                    >
                      <Text style={[styles.tabText, styles.activeTabText]}>
                        অফলাইন কার্যতালিকা
                      </Text>
                      <AnimatedArrow />
                    </Pressable>
                  </View>

                  <View style={styles.contentBox}>

                      <View style={{ zIndex: -1, marginTop: IS_DESKTOP_FORM ?  -40 : 40 }}>
                      <Text style={styles.headerText}>
                        {courtName ? `${courtName} ` : ''}
                        {/* {courtOfficeName ? `${courtOfficeName},` : ''}{' '} */}
                        {/* {courtName ? districtName : ""} */}
                      </Text>
                      <Text style={styles.headerText}>দৈনিক কার্যতালিকা</Text>

                      {!isLoading && IS_WEB_WIDE && activeDate && (
                        <Text style={styles.headerDateText}>
                          তারিখঃ{' '}
                          {replaceTextDate(moment(activeDate).format('DD-MM-YYYY'))},{' '}
                          {displayDay}
                        </Text>
                      )}
                    </View>

                    <ImageGallery
                      grAllData={imageList}
                      date={activeDate ?? new Date()}
                    />
                    <View style={{ marginBottom: 100 }} />

                  </View>
                </View>
              )}
            </View>
          </View>
        </TouchableWithoutFeedback>
      </View>

      <Modal visible={imageModalVisible} transparent animationType="fade">
        <View style={styles.modalContainer}>
          <Pressable style={styles.modalCloseButton} onPress={handleCloseModal}>
            <Text style={styles.modalCloseText}>✕</Text>
          </Pressable>

          <TouchableOpacity activeOpacity={1} style={styles.fullImageWrapper}>
            {selectedImage?.file_path ? (
              <Image
                style={styles.fullImage}
                source={{
                  uri: `https://api.bdjudgecourt.com${selectedImage.file_path}`,
                }}
              />
            ) : null}
          </TouchableOpacity>
        </View>
      </Modal>
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  searchContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    alignItems: 'center',
    paddingVertical: 0,
  },
  headerFixed: {
    top: 0,
    left: 0,
    zIndex: 2,
    position: 'sticky',
    backgroundColor: '#fff',
  },
  backgroundImage: {
    flex: 1,
    height: 'auto',
    position: 'absolute',
    width: windowWidth - 190,
    bottom: 0,
    zIndex: -1,
  },
  formGroup: {
    width: IS_DESKTOP_FORM ? 'auto' : IS_TABLET_FORM ? '50%' : '100%',
    paddingHorizontal: 5,
    marginBottom: IS_WEB_WIDE ? -2 : 10,
    position: "relative",
    minWidth: 0,
    marginTop: IS_WEB_WIDE ? 3 : 10,
    marginBottom: 3
  },
  pickerWrapper: {
    width: IS_DESKTOP_FORM ? 'auto' : '100%',
    borderColor: '#ccc',
    borderWidth: 1,
    borderRadius: 4,
    backgroundColor: 'transparent',
  },
  picker: {
    height: windowWidth >= 600 ? 27 : 40,
    color: '#333',
    width: IS_DESKTOP_FORM ? 100 : '100%',
    backgroundColor: '#fff',
    fontFamily: 'SolaimanLipi',
    fontSize:14
  },
  pickerPlaceholder: {
    height: windowWidth >= 600 ? 27 : 40,
    color: '#989898',
    width: IS_DESKTOP_FORM ? 100 : '100%',
    backgroundColor: '#fff',
    fontFamily: 'SolaimanLipi',
    fontSize: 14,
  },
  errorWrapper: {
    position: 'absolute',
    width: '100%',
    top: IS_DESKTOP_FORM ? 30 : 40,
    left: 5,
  },
  dropdownOpenGroup: {
    zIndex: 999999,
  },
  dropdownOpenWrapper: {
    zIndex: 999999,
    overflow: 'visible',
  },
  dateFieldRow: {
    flexDirection: 'row',
    width: IS_DESKTOP_FORM ? 'auto' : '100%',
  },
  dateInputWrapper: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ccc',
    height: windowWidth >= 600 ? 27 : 41,
    borderRadius: 5,
    margin: 0,
    zIndex: -1,
    width: IS_DESKTOP_FORM ? 'auto' : '100%',
  },
  dateInput: {
    zIndex: -1,
    height: windowWidth >= 600 ? 27 : 40,
    padding: 5,
    borderRadius: 2,
    fontSize: 13,
    width: IS_DESKTOP_FORM ? 90 : '100%',
    flex: IS_DESKTOP_FORM ? 0 : 1,
    backgroundColor: '#fff',
    fontFamily: 'SolaimanLipi',
  },
  calendarIcon: {
    padding: 10,
    margin: 5,
    height: 25,
    width: 25,
    resizeMode: 'stretch',
    alignItems: 'center',
  },
  searchButtonWrapper: {
    marginTop: IS_WEB_WIDE ? -7 : 10,
    marginLeft: IS_WEB_WIDE ? 0 : 35,
    zIndex: -1,
  },
  resultsWrapper: {
    marginTop: 20,
    marginHorizontal: 16,
    zIndex: -1,
  },
  tabWrapper: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingBottom: 0,
    zIndex: 2,
    position: 'relative',
  },
  tabButton: {
    paddingVertical: 7,
    paddingHorizontal: 10,
    marginRight: 10,
    borderRadius: 5,
    backgroundColor: '#000',
    borderWidth: 1,
    // borderColor: '#FF5573',
    cursor: 'pointer',
    zIndex: 999,
  },
  activeTabButton: {
    backgroundColor: '#000',
    // borderColor: '#FF5573',
    shadowColor: '#3b82f6',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.18,
    shadowRadius: 10,
    elevation: 8,
    zIndex: 999,
  },
  tabText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
    fontFamily: 'SolaimanLipi',
  },
  activeTabText: {
    color: '#fff',
    fontWeight: '600',
    fontFamily: 'SolaimanLipi',
  },
  contentBox: {
    backgroundColor: '#ffffff',
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
    borderTopRightRadius: 24,
    padding: 24,
    paddingTop: 0,
    height: windowHeight - 100,
  },
  headerText: {
    textAlign: 'center',
    fontSize: 20,
    color: '#000',
    fontFamily: 'SolaimanLipi',
  },
  headerDateText: {
    textAlign: 'center',
    fontSize: 16,
    color: '#000',
    fontFamily: 'SolaimanLipi',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.95)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullImageWrapper: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullImage: {
    width: '99%',
    height: '90%',
    resizeMode: 'contain',
  },
  modalCloseButton: {
    position: 'absolute',
    top: 50,
    right: 25,
    zIndex: 10,
    backgroundColor: 'rgba(255,255,255,0.15)',
    width: 42,
    height: 42,
    borderRadius: 21,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalCloseText: {
    color: '#fff',
    fontSize: 22,
    fontWeight: 'bold',
  },
  imageRowItem: {
    width: '31.33%',
    aspectRatio: 1,
    backgroundColor: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
  },
  imageRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 0,
    paddingHorizontal: 10,
    backgroundColor: '#fff',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 50,
  },
  emptyText: {
    fontSize: 16,
    color: '#999',
    fontWeight: '500',
  },
});
